import type { BoxDef, DecorDef, MapDef, Tuple3 } from './types.js';

/**
 * Kokun City — primer mapa jugable.
 *
 * Ejes: X = este, Z = sur, Y = arriba. Unidades en metros.
 *
 *                    N (z negativo)
 *        [Ayuntamiento]        [Pizzería]+escalera de cajas
 *                  [Generador]
 *  [Patio oculto][Bromas S.A.]  (PLAZA + fuente)     [Parque + lago helado + pantalla]
 *        [Torre de pisos]                 [Café Caos]
 *                  [Zona de entrenamiento + torre]
 *                    S (z positivo)
 */

const boxes: BoxDef[] = [];
const decor: DecorDef[] = [];

const box = (id: string, min: Tuple3, max: Tuple3, style: BoxDef['style'], extra: Partial<BoxDef> = {}) =>
  boxes.push({ id, min, max, style, ...extra });

// Suelo y límites del mundo
box('ground', [-70, -2, -70], [70, 0, 70], 'ground');
box('bound-n', [-71, 0, -71], [71, 30, -70], 'invisible');
box('bound-s', [-71, 0, 70], [71, 30, 71], 'invisible');
box('bound-w', [-71, 0, -70], [-70, 30, 70], 'invisible');
box('bound-e', [70, 0, -70], [71, 30, 70], 'invisible');

// Fuente central
box('fountain-basin', [-3, 0, -3], [3, 0.8, 3], 'fountain');
box('fountain-pillar', [-0.6, 0.8, -0.6], [0.6, 3.2, 0.6], 'fountain', { color: 0xffd166 });

// Generador (misión «Reactivar la ciudad»)
box('generator-base', [-5, 0, -25], [5, 0.3, -15], 'generator', { color: 0x5c677d });
box('generator-core', [-1.6, 0.3, -23.5], [1.6, 4.2, -20.5], 'generator', { color: 0x3a86ff });

// Pedestal del botón rojo
box('red-button-pedestal', [5.5, 0, 3.5], [6.5, 0.9, 4.5], 'pedestal', { color: 0xf5f5f5 });

// Escalera de cajas detrás de la pizzería (atajo a la azotea)
[1.2, 2.4, 3.6, 4.8, 6.0].forEach((h, i) => {
  const z0 = -29 - i * 2;
  box(`pizza-step-${i}`, [30, 0, z0 - 2], [32.5, h, z0], 'steps', { color: 0xc68b59 });
});

// Patio oculto (oeste), con una entrada estrecha detrás de los arbustos
box('yard-wall-n', [-54.5, 0, -10.5], [-42, 3, -10], 'wall');
box('yard-wall-s', [-54.5, 0, 4], [-42, 3, 4.5], 'wall');
box('yard-wall-w', [-54.5, 0, -10], [-54, 3, 4], 'wall');
box('yard-wall-e1', [-42.5, 0, -10], [-42, 3, -4], 'wall');
box('yard-wall-e2', [-42.5, 0, -2.8], [-42, 3, 4], 'wall');

// Lago helado del parque: superficie resbaladiza permanente
box('ice-pond', [44, 0, -12], [53, 0.06, -5], 'ice', { surface: 'ice' });

// Pantalla de calibración (postes)
box('display-post-l', [51.6, 0, -3.2], [52.2, 2, -2.6], 'post');
box('display-post-r', [51.6, 0, 2.6], [52.2, 2, 3.2], 'post');

// Bancos del parque
[
  [40, 8],
  [46, 10],
  [61, 12],
  [40, -9],
].forEach(([x, z], i) => box(`bench-${i}`, [x - 1, 0, z - 0.3], [x + 1, 0.5, z + 0.3], 'bench'));

// Zona de entrenamiento: recorrido de plataformas hasta la torre
box('train-p1', [-11.5, 0, 38.5], [-8.5, 1.2, 41.5], 'platform', { color: 0xff6b6b });
box('train-p2', [-7.5, 0, 42.5], [-4.5, 2.4, 45.5], 'platform', { color: 0xffa94d });
box('train-p3', [-3.5, 0, 45.5], [-0.5, 3.6, 48.5], 'platform', { color: 0xffd43b });
box('train-p4', [2.5, 0, 48.5], [5.5, 4.8, 51.5], 'platform', { color: 0x69db7c });
box('train-p5', [3.5, 0, 59], [6.5, 7.6, 62], 'platform', { color: 0x4dabf7 });
box('train-tower', [-2.5, 0, 55.5], [2.5, 9, 60.5], 'platform', { color: 0x9775fa });
box('train-padtop', [-13, 0, 51], [-10, 6.8, 54], 'platform', { color: 0xf783ac });
box('train-pad', [-13.4, 0, 45.6], [-10.6, 0.15, 48.4], 'jumpPad', { surface: 'jumpPad' });
box('pizza-pad', [11.6, 0, -35.4], [14.4, 0.15, -32.6], 'jumpPad', { surface: 'jumpPad' });

// Árboles (tronco con colisión + copa decorativa)
const trees: Array<[number, number, number?]> = [
  [38, -14],
  [42, 14],
  [50, 13],
  [60, -12],
  [62, 2],
  [56, 14],
  [36, 2],
  [-20, 12],
  [20, 10],
  [-58, -30],
  [-60, 30],
  [-46, 44],
  [48, 44],
  [60, 34],
  [-62, 54],
  [30, 56],
  [-30, 56],
  [58, -40],
  [44, -54],
  [-50, -54],
];
trees.forEach(([x, z], i) => {
  box(`trunk-${i}`, [x - 0.3, 0, z - 0.3], [x + 0.3, 3, z + 0.3], 'trunk');
  decor.push({ kind: i % 3 === 0 ? 'pine' : 'tree', pos: [x, 0, z], scale: 0.9 + ((i * 37) % 10) / 20 });
});

// Farolas (decorativas, colisión fina)
const lamps: Array<[number, number]> = [
  [10, -10],
  [-10, -12],
  [12, 12],
  [-12, 14],
  [0, -28],
  [24, 0],
  [-22, 0],
  [0, 30],
];
lamps.forEach(([x, z], i) => {
  box(`lamp-${i}`, [x - 0.15, 0, z - 0.15], [x + 0.15, 3.6, z + 0.15], 'post', { color: 0x343a40 });
  decor.push({ kind: 'lamp', pos: [x, 0, z] });
});

// Arbustos (sin colisión: sirven para esconderse)
const bushes: Array<[number, number]> = [
  [-41, -3.4],
  [-40.6, -1.6],
  [44, 4],
  [46, 4.6],
  [58, -4],
  [36, -6],
  [-16, 22],
  [16, 22],
  [10, -40],
];
bushes.forEach(([x, z]) => decor.push({ kind: 'bush', pos: [x, 0, z], scale: 1.3 }));

decor.push(
  { kind: 'pond', pos: [56, 0.02, 8], scale: 4 },
  { kind: 'clockTower', pos: [-25, 10, -37] },
  { kind: 'flowers', pos: [-8, 0, -8] },
  { kind: 'flowers', pos: [8, 0, -8] },
  { kind: 'flowers', pos: [-8, 0, 8] },
  { kind: 'flowers', pos: [40, 0, 0] },
  { kind: 'umbrella', pos: [20, 0, 24], color: 0xff6b6b },
  { kind: 'umbrella', pos: [20, 0, 30], color: 0x4dabf7 },
  { kind: 'balloon', pos: [-6, 0, 36], color: 0xff006e },
  { kind: 'balloon', pos: [6, 0, 36], color: 0x8338ec },
  ...([
    [-40, 25, -20],
    [30, 30, 10],
    [0, 28, 60],
    [60, 32, -50],
    [-60, 27, 40],
    [10, 34, -60],
  ] as Tuple3[]).map((pos, i) => ({ kind: 'cloud' as const, pos, scale: 1 + (i % 3) * 0.4 })),
);

const ring = (r: number, n: number, y = 0.1, offset = 0): Tuple3[] =>
  Array.from({ length: n }, (_, i) => {
    const a = offset + (i / n) * Math.PI * 2;
    return [Math.round(Math.cos(a) * r * 100) / 100, y, Math.round(Math.sin(a) * r * 100) / 100];
  });

export const KOKUN_CITY: MapDef = {
  id: 'kokun-city',
  name: 'Kokun City',
  halfSize: 70,
  killY: -15,
  boxes,
  buildings: [
    {
      id: 'townhall',
      name: 'Ayuntamiento',
      min: [-34, -44],
      max: [-16, -30],
      height: 10,
      color: 0xfff1d6,
      roofColor: 0xe63946,
      roof: 'flat',
      sign: 'AYUNTAMIENTO',
      signColor: 0x1d3557,
    },
    {
      id: 'pizzeria',
      name: 'Pizza Kokun',
      min: [16, -40],
      max: [30, -28],
      height: 7,
      color: 0xffb4a2,
      roofColor: 0x6d597a,
      roof: 'flat',
      sign: 'PIZZA KOKUN',
      signColor: 0xe76f51,
    },
    {
      id: 'pranks',
      name: 'Bromas S.A.',
      min: [-40, -12],
      max: [-28, 4],
      height: 8,
      color: 0xb8f2e6,
      roofColor: 0xff006e,
      roof: 'gable',
      sign: 'BROMAS S.A.',
      signColor: 0x8338ec,
    },
    {
      id: 'apartments',
      name: 'Torre Kokun',
      min: [-40, 18],
      max: [-26, 34],
      height: 14,
      color: 0xcdb4db,
      roofColor: 0x5e548e,
      roof: 'flat',
    },
    {
      id: 'cafe',
      name: 'Café Caos',
      min: [22, 22],
      max: [34, 34],
      height: 6,
      color: 0xfefae0,
      roofColor: 0x2a9d8f,
      roof: 'gable',
      sign: 'CAFÉ CAOS',
      signColor: 0x264653,
    },
    {
      id: 'lab',
      name: 'Laboratorio del Caos',
      min: [-12, -64],
      max: [8, -52],
      height: 8,
      color: 0xdee2e6,
      roofColor: 0x80ffdb,
      roof: 'dome',
      sign: 'LABORATORIO DEL CAOS · PRÓXIMAMENTE',
      signColor: 0x7209b7,
    },
  ],
  movingPlatforms: [
    {
      id: 'train-mover',
      size: [3, 0.5, 3],
      from: [8.5, 5.75, 51.5],
      to: [8.5, 5.75, 58],
      period: 7,
      phase: 0,
      color: 0x20c997,
    },
    {
      id: 'plaza-lift',
      size: [3, 0.5, 3],
      from: [-20, 0.25, -28.4],
      to: [-20, 9.75, -28.4],
      period: 10,
      phase: 0.25,
      color: 0xfcc419,
    },
  ],
  vanishPlatforms: [
    { id: 'bridge-1', min: [-9.4, 6.4, 51.8], max: [-7, 6.8, 54.2], color: 0xffe066 },
    { id: 'bridge-2', min: [-6.6, 7.2, 53.4], max: [-4.2, 7.6, 55.8], color: 0xffe066 },
  ],
  crates: [
    { id: 'crate-1', pos: [-16, 0, 14], size: 1.2 },
    { id: 'crate-2', pos: [18, 0, 16], size: 1.2 },
    { id: 'crate-3', pos: [4, 0, 20], size: 1.2 },
  ],
  interactables: [
    { id: 'cell-a', kind: 'cellSpawn', pos: [24, 7, -34], radius: 1.6, label: 'Célula de energía' },
    { id: 'cell-b', kind: 'cellSpawn', pos: [-48, 0, -3], radius: 1.6, label: 'Célula de energía' },
    { id: 'cell-c', kind: 'cellSpawn', pos: [0, 9, 58], radius: 1.6, label: 'Célula de energía' },
    { id: 'socket', kind: 'socket', pos: [0, 0.3, -18.6], radius: 2.6, label: 'Ranura del generador' },
    { id: 'valve-1', kind: 'valve', pos: [-4, 0.3, -21], radius: 1.6, label: 'Válvula izquierda' },
    { id: 'valve-2', kind: 'valve', pos: [0, 0.3, -24.3], radius: 1.6, label: 'Válvula trasera' },
    { id: 'valve-3', kind: 'valve', pos: [4, 0.3, -21], radius: 1.6, label: 'Válvula derecha' },
    { id: 'lever', kind: 'lever', pos: [3.2, 0.3, -17.2], radius: 1.6, label: 'Palanca de arranque' },
    { id: 'plate-west', kind: 'plate', pos: [-10, 0, 10], radius: 1.3, label: 'Placa oeste' },
    { id: 'plate-east', kind: 'plate', pos: [14, 0, 10], radius: 1.3, label: 'Placa este' },
    { id: 'display', kind: 'display', pos: [51.9, 3.6, 0], radius: 0, label: 'Pantalla de calibración', rotY: -Math.PI / 2 },
    { id: 'red-button', kind: 'redButton', pos: [6, 0.9, 4], radius: 1.8, label: 'Botón rojo (¡no tocar!)' },
    { id: 'tp-plaza', kind: 'teleporter', pos: [-12, 0, -6], radius: 1.1, label: 'Teletransportador sorpresa' },
    { id: 'tp-yard', kind: 'teleporter', pos: [-51, 0, 1.5], radius: 1.1, label: 'Teletransportador sorpresa' },
    { id: 'vanish-switch', kind: 'vanishSwitch', pos: [-4, 0, 38], radius: 1.6, label: 'Interruptor del puente' },
  ],
  decor,
  zones: [
    { id: 'gen-ring', kind: 'generatorRing', pos: [0, 0, -19.5], radius: 6.5 },
    ...bushes.map(([x, z], i) => ({ id: `hide-${i}`, kind: 'hide' as const, pos: [x, 0, z] as Tuple3, radius: 1.3 })),
  ],
  paint: [
    { kind: 'plaza', min: [-15, -15], max: [15, 15], circle: true },
    { kind: 'road', min: [-70, -14], max: [70, -10] },
    { kind: 'road', min: [-70, 16], max: [70, 20] },
    { kind: 'road', min: [-14, -70], max: [-10, 70] },
    { kind: 'road', min: [10, -70], max: [14, 70] },
    { kind: 'grass', min: [34, -18], max: [66, 18] },
    { kind: 'sand', min: [-17, 34], max: [17, 66] },
  ],
  spawnPoints: ring(7, 12, 0.1, 0.2),
  safePoints: [
    ...ring(9, 6, 0.1),
    [40, 0.1, 0],
    [0, 0.1, 32],
    [-20, 0.1, 8],
    [20, 0.1, -20],
    [-46, 0.1, -16],
    [30, 0.1, 40],
  ],
  coinArea: { min: [-15, -13], max: [15, 15] },
};
